<template>
  <div class="modal-card" :class="$style.container">
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ isEdit ? 'Edit' : 'Add' }} Participant
        </p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body" :class="$style.body">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|email"
          name="email"
        >
          <b-field
            v-if="!isEdit"
            label="Email"
            :message="errors[0]"
            :type="errors.length ? 'is-danger' : undefined"
            :class="$style.fieldContainer"
          >
            <b-dropdown :class="$style.dropdownContainer">
              <b-input
                :disabled="isEdit"
                type="email"
                v-model="form.userEmail"
                placeholder=""
                required
                :class="$style.triggerButton"
                slot="trigger"
              />

              <b-dropdown-item
                v-for="projectParticipant in filteredProjectParticipants"
                :key="projectParticipant.userEmail"
                :class="$style.listItem"
                @click="handleSelectProjectParticipant(projectParticipant)"
              >
                <div>
                  <p>{{ projectParticipant.userEmail }}</p>
                  <p>{{ projectParticipant.participantRole }}</p>
                </div>
                <div
                  v-if="projectParticipant.isPending"
                  :class="$style.pendingTabText"
                >
                  PENDING INVITATION
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
          <b-field
            v-else
            label="Email"
            :message="errors[0]"
            :type="errors.length ? 'is-danger' : undefined"
            :class="$style.fieldContainer"
          >
            <b-input
              :disabled="isEdit"
              type="email"
              v-model="form.userEmail"
              placeholder=""
              required
            >
            </b-input>
          </b-field>
        </ValidationProvider>

        <b-field label="Role">
          <b-dropdown disabled :class="$style.dropdownContainer">
            <button class="button" :class="$style.triggerButton" slot="trigger">
              <span>{{ roleOptions[0].value }}</span>
              <b-icon type="is-black" icon="menu-down" size="is-small"></b-icon>
            </button>
            <b-dropdown-item aria-role="listitem">{{
              roleOptions[0].value
            }}</b-dropdown-item>
          </b-dropdown>
        </b-field>

        <ValidationProvider v-slot="{ errors }" rules="required" name="site">
          <b-field
            label="Site"
            :message="errors[0]"
            :type="errors.length ? 'is-danger' : undefined"
          >
            <b-dropdown :multiple="true" :class="$style.dropdownContainer">
              <button
                class="button"
                :class="$style.triggerButton"
                slot="trigger"
              >
                <span
                  v-if="selectedSiteNames.length > 65 && !checkedAll"
                  class="text-input"
                >
                  <b-tooltip
                    :label="selectedSiteNames"
                    position="is-top"
                    size="is-small"
                  >
                    {{ displaySelectedSiteName }}
                  </b-tooltip>
                </span>
                <span v-else class="text-input">
                  {{ displaySelectedSiteName }}
                </span>
                <b-icon
                  type="is-black"
                  icon="menu-down"
                  size="is-small"
                ></b-icon>
              </button>

              <b-dropdown-item :custom="true">
                <label>
                  <div :class="$style.item">
                    <input
                      type="checkbox"
                      :checked="checkedAll"
                      @input="($event) => handleCheckAll($event.target.checked)"
                    />
                    <b>All Sites</b>
                  </div>
                </label>
              </b-dropdown-item>

              <b-dropdown-item
                v-for="(item, index) in siteOptions"
                :key="`options${index}`"
                :custom="true"
              >
                <label>
                  <div :class="$style.item">
                    <input
                      type="checkbox"
                      :value="item"
                      v-model="form.participantSites"
                    />
                    <p>
                      {{ item.name }} <br />
                      <span>{{ item.address }}</span>
                    </p>
                  </div>
                </label>
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </ValidationProvider>
        <b-notification
          :class="[$style.customErrorContainer, 'text-size']"
          type="is-danger is-light"
          :closable="false"
          v-if="showErrorMessage"
        >
          <span v-if="isSameProjectOwnerEmail">
            User email cannot be same as project owner email.
          </span>
          <span v-if="isSameParticipantEmail && existingParticipant">
            The participant with this email already added as a
            <span style="font-weight: bold">
              {{ existingParticipant.participantRole }}
            </span>
            to the following site(s):
            <ul :class="$style.errorSiteList">
              <li
                v-for="(item, index) in existingParticipant.participantSites"
                :key="`sites${index}`"
                style="font-weight: bold"
              >
                {{ getSiteName(item.site) }}
              </li>
            </ul>
          </span>
        </b-notification>
      </section>

      <footer class="modal-card-foot">
        <button
          class="button is-primary is-rounded"
          @click="handleSubmit"
          :disabled="invalid"
        >
          {{ isEdit ? 'Save' : 'Add Participant' }}
        </button>
      </footer>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import BaseDropDown from '@/components/base/BaseDropDown.vue';
import { Site } from '@/store/modules/site/types/site.types';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  CreateAuditParticipantPayload,
  UpdateAuditParticipantPayload,
} from '../../../store/modules/audits/types/audits.types';
import { cloneData } from '@/utils/general.util';
import { PacesParticipantRoles } from '@/jbi-shared/types/paces-project.types';
import {
  ParticipantListDTO,
  ParticipantSitesDTO,
  ParticipationLevelEnum,
} from '@/store/modules/projects/types/projects.types';

@Component({
  components: {
    BaseDropDown,
    ValidationProvider,
    ValidationObserver,
  },
})
export default class AuditParticipantForm extends Vue {
  @Prop() public participant!: ParticipantListDTO;
  @Prop({ required: true }) public isEdit!: boolean;
  @Prop() public sites!: Site[];
  @Prop() public projectOwnerEmail!: string;
  @Prop() public participantsList!: ParticipantListDTO[]; // audit-level
  @Prop() public projectParticipantList!: ParticipantListDTO[];

  @Action('audits/createAuditParticipant')
  public createAuditParticipant!: (
    payload: CreateAuditParticipantPayload,
  ) => void;

  @Action('audits/updateAuditParticipant')
  public updateAuditParticipant!: (
    payload: UpdateAuditParticipantPayload,
  ) => void;

  public isSameProjectOwnerEmail: boolean = false;
  public isSameParticipantEmail: boolean = false;
  public existingParticipant: ParticipantListDTO | undefined;
  public showErrorMessage: boolean = false;
  public form = {
    userEmail: '',
    participantRole: null as any,
    participantSites: [] as Site[],
  };

  get auditId() {
    return +this.$route.params.auditId;
  }

  get filteredProjectParticipants() {
    if (!this.form.userEmail || !this.projectParticipantList.length) {
      return [];
    }

    const auditParticipantEmails: string[] = this.participantsList
      .filter(
        (auditParticipant) =>
          auditParticipant.participantRole ===
          PacesParticipantRoles.DataCollector,
      )
      .map((auditParticipant) => auditParticipant.userEmail);

    const formUserEmail: string = this.form.userEmail.trim().toLowerCase();

    return (
      this.projectParticipantList
        .filter((pp) => {
          const projectParticipantEmail: string = pp.userEmail
            .trim()
            .toLowerCase();

          return (
            !auditParticipantEmails.includes(projectParticipantEmail) &&
            projectParticipantEmail.includes(formUserEmail) &&
            pp.participantRole === PacesParticipantRoles.DataCollector
          );
        })
        .map((pp) => {
          const data = {
            ...pp,
            isPending: pp.participantSites.some((ps) => ps.invitationId),
          };
          return data;
        }) || []
    );
  }

  get roleOptions() {
    return [
      {
        label: PacesParticipantRoles.DataCollector,
        value: PacesParticipantRoles.DataCollector,
      },
    ];
  }

  get siteOptions() {
    if (Array.isArray(this.sites)) {
      return this.sites;
    }
    return [];
  }

  get displaySelectedSiteName() {
    if (this.form.participantSites.length > 0) {
      const siteNames: Site[] = [];
      this.form.participantSites.map((site: Site) => {
        const selectedAuditSite = this.siteOptions.find(
          (option: Site) => site.id === option.id,
        );
        if (selectedAuditSite) {
          siteNames.push(selectedAuditSite);
        }
      });
      if (siteNames) {
        this.selectedSiteNames = this.convertArrayItemIntoString(siteNames);
      }
      const selectedSiteName =
        this.selectedSiteNames.length > 65
          ? this.selectedSiteNames.substring(0, 65) + '...'
          : this.selectedSiteNames;
      return this.checkedAll ? 'All Sites' : selectedSiteName;
    } else {
      return 'Select Sites';
    }
  }

  public checkedAll = false;
  public selectedSiteNames: string = '';

  getSiteName(id: number): string | null {
    const site = this.sites.find((site) => site.id === id);
    return site ? site.name : null;
  }

  public mounted() {
    if (this.participant) {
      const participant: ParticipantListDTO = cloneData(this.participant);
      const { userEmail, participantRole, participantSites } = participant;

      this.form.userEmail = userEmail;
      this.form.participantRole =
        this.roleOptions.find(({ value }) => value === participantRole) || null;

      const data: Site[] = [];
      participantSites.forEach((participantSite: ParticipantSitesDTO) => {
        const site = this.siteOptions.find(
          (siteOption) => siteOption.id === participantSite.site,
        );
        if (site) {
          data.push(site);
        }
      });
      this.form.participantSites = data;
    } else {
      // this.form.participantSites = cloneData(this.siteOptions);
      this.form.participantRole = this.roleOptions[0];
    }
  }

  public handleSelectProjectParticipant(
    projectParticipant: ParticipantListDTO,
  ) {
    this.form.userEmail = projectParticipant.userEmail;
  }

  public handleCheckAll(value: boolean) {
    this.checkedAll = value;
    this.form.participantSites = [];
    if (value) {
      this.form.participantSites = cloneData(this.siteOptions);
    }
  }

  public async handleSubmit() {
    const assignedSiteIds = this.form.participantSites.map(
      (participantSite: Site) => +participantSite.id,
    );

    if (
      this.isSameProjectOwnerEmail ||
      this.checkParticipantExistsByRoleAndSite()
    ) {
      this.showErrorMessage = true;
    } else if (this.isEdit) {
      const existingSiteIds: number[] = this.participant.participantSites.map(
        (ps: ParticipantSitesDTO) => ps.site,
      );

      this.updateAuditParticipant({
        auditId: this.auditId,
        email: this.form.userEmail,
        assignedRole: this.form.participantRole.value,
        assignedSiteIds,
        newSiteIds: assignedSiteIds.filter(
          (siteId: number) => !existingSiteIds.includes(siteId),
        ),
        removeSiteIds: existingSiteIds.filter(
          (siteId: number) => !assignedSiteIds.includes(siteId),
        ),
        isRoleChanged: Boolean(
          this.participant.participantRole !== this.form.participantRole.value,
        ),
        level: ParticipationLevelEnum.Audit,
      });
      this.$emit('close');
    } else {
      this.createAuditParticipant({
        auditId: this.auditId,
        email: this.form.userEmail,
        assignedRole: this.form.participantRole.value,
        assignedSiteIds,
        level: ParticipationLevelEnum.Audit,
      });
      this.$emit('close');
    }
  }

  public convertArrayItemIntoString(data: any[]) {
    const values = data.map((item: any) => `${item.name}`).join(', ');
    return `${values}`;
  }

  @Watch('form.participantSites', { deep: true })
  public onCheck(value: any[]) {
    this.checkedAll = false;
    if (
      value
        .map(({ id }) => id)
        .sort()
        .toString() ===
      this.siteOptions
        .map(({ id }) => id)
        .sort()
        .toString()
    ) {
      this.checkedAll = true;
    }
  }

  @Watch('form.userEmail', { deep: true })
  public onCheckUserEmail(participantEmail: string) {
    participantEmail = participantEmail.toLowerCase();
    if (this.participantsList) {
      this.existingParticipant = Object(this.participantsList).find(
        (data: ParticipantListDTO) => {
          let matchingCondition: boolean =
            data.userEmail.toLowerCase() === participantEmail;
          // While editing avoid checking the conditions with the same participant
          if (this.isEdit) {
            matchingCondition =
              matchingCondition &&
              this.participant.participantRole !== data.participantRole;
          }
          return matchingCondition;
        },
      );
    }

    switch (participantEmail) {
      case this.projectOwnerEmail.toLowerCase():
        this.isSameProjectOwnerEmail = true;
        break;
      case this.existingParticipant &&
        this.existingParticipant.userEmail.toLowerCase():
        this.isSameParticipantEmail = true;
        break;
      default:
        this.isSameParticipantEmail = false;
        this.isSameProjectOwnerEmail = false;
        this.showErrorMessage = false;
    }
  }

  /**
   * Following scenarios are handled while checking if the participants exists or not
   *
   * 1. If the participant roles are the same then the participant exists.
   * 2. The participant exists if at least on of the sites match with the existing participant
   */
  private checkParticipantExistsByRoleAndSite(): boolean {
    const existingParticipant: ParticipantListDTO | undefined = this
      .existingParticipant;

    // Condition: If the participant roles are the same then the participant exists
    if (
      existingParticipant &&
      this.form.participantRole.value === existingParticipant.participantRole
    ) {
      return true;
    }

    // Condition: The participant exists if at least on of the sites match with the existing participant
    let isSiteSame: boolean = false;
    if (existingParticipant) {
      isSiteSame = existingParticipant.participantSites.some(
        (existingParticipantSite: ParticipantSitesDTO) => {
          return Boolean(
            this.form.participantSites.find(
              (site: Site) => site.id === existingParticipantSite.site,
            ),
          );
        },
      );
    }
    return Boolean(isSiteSame);
  }
}
</script>

<style lang="scss" module>
.body {
  min-height: 300px;
}

.triggerButton {
  width: 100%;
  justify-content: space-between;
}

.container {
  :global(.modal-card-body) {
    overflow: visible;
  }

  .triggerButton {
    width: 100%;
    justify-content: space-between;

    span {
      font-weight: normal;
    }
  }

  .dropdownContainer {
    :global(.dropdown-menu) {
      padding-top: 0;
      overflow-y: auto;
      max-height: 250px;

      :global(.dropdown-content) {
        padding-top: 0;
        padding-bottom: 0;

        :global(.dropdown-item) {
          padding: 0rem;
          border-bottom: 0.5px solid $grey-border;

          &:hover,
          label {
            background: #f7fbff;
            cursor: pointer;
          }

          &:last-child {
            border-bottom: none;
          }
        }
      }
    }

    .item {
      padding: 0.75rem;
      display: flex;

      input {
        margin-right: 1rem;
      }

      span {
        color: #9f9f9f;
      }
    }
  }
}

.listItem {
  padding: 0.75rem !important;
  display: flex;
  gap: 12px;
}

.customErrorContainer {
  background-color: #feecf0 !important;
  div > div {
    color: #cc0f35;
  }
}

.errorSiteList {
  list-style-type: circle;
  list-style-position: inside;
  margin-left: 15px;
}

.pendingTabText {
  color: #ff9700;
  background-color: #fff5e6;
  border-radius: 4px;
  font-size: 10px;
  margin-right: 0.5rem;
  height: fit-content;
  width: fit-content;
  padding: 0 4px;
}
</style>
